import React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useCountdown } from "../hooks/useCountdown";
import { getEventStatus, STATUS_LABELS, resolveImageUrl } from "../lib/eventsUtils";
import {
  Calendar,
  Clock,
  Sword,
  User,
  Timer,
  Calculator as CalcIcon,
  CalendarCheck,
  ImageOff,
  CheckCircle2,
  ListChecks,
} from "lucide-react";

const formatDateTime = (iso) =>
  new Intl.DateTimeFormat("ar-EG", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));

const formatSaudiTime = (iso) =>
  new Intl.DateTimeFormat("ar-SA", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Riyadh",
  }).format(new Date(iso));

const StatusPill = ({ status }) => {
  const map = {
    upcoming: "bg-[#1E3A66]/70 border-[#4A6FA5]/40 text-[#B9CDE8]",
    live: "bg-[#0F3A2A]/70 border-emerald-600/40 text-emerald-200",
    ended: "bg-slate-800/70 border-slate-600/40 text-slate-300",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${map[status]}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
};

const CountdownRow = ({ target, label }) => {
  const { days, hours, minutes, seconds } = useCountdown(target);
  return (
    <div className="rounded-xl bg-[#0A1128]/60 border border-[#C9A96A]/15 p-3">
      <div className="flex items-center gap-1.5 mb-2 text-slate-400 text-xs">
        <Clock className="w-4 h-4" />
        <span>{label}</span>
      </div>
      <div className="grid grid-cols-4 gap-2">
        {[
          [days, "يوم"],
          [hours, "ساعة"],
          [minutes, "دقيقة"],
          [seconds, "ثانية"],
        ].map(([v, l]) => (
          <div
            key={l}
            className="text-center rounded-lg bg-white/[0.02] border border-white/5 py-2"
          >
            <div className="text-white font-black text-lg tabular-nums leading-none">
              {String(v).padStart(2, "0")}
            </div>
            <div className="text-[10px] text-slate-400 mt-1">{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// صف بيانات بسيط — يقبل قيمة نصية أو مصفوفة أو null
const Row = ({ icon: Icon, label, value, mono }) => {
  if (value == null || (Array.isArray(value) && value.length === 0)) return null;

  const renderValue = () => {
    if (Array.isArray(value)) {
      return (
        <ul className="space-y-1 mt-0.5" dir={mono ? "ltr" : "auto"}>
          {value.map((v, i) => (
            <li
              key={i}
              className="flex items-start gap-1.5 text-slate-100 text-sm font-semibold"
            >
              <span className="w-1 h-1 rounded-full bg-[#C9A96A] mt-2 flex-shrink-0" />
              <span className={mono ? "tracking-wide" : ""}>{v}</span>
            </li>
          ))}
        </ul>
      );
    }
    return (
      <div
        className={`text-slate-100 text-sm font-semibold ${mono ? "tracking-wide" : ""}`}
        dir={mono ? "ltr" : "auto"}
      >
        {value}
      </div>
    );
  };

  return (
    <div className="flex items-start gap-2.5">
      <div className="w-8 h-8 rounded-lg bg-white/[0.03] border border-white/5 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-[#C9A96A]" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-slate-400 text-[11px]">{label}</div>
        <div dir={mono ? "ltr" : "rtl"}>{renderValue()}</div>
      </div>
    </div>
  );
};

// صف تاريخ مع سطر توقيت السعودية
const DateRow = ({ icon, label, iso }) => {
  if (!iso) {
    return (
      <Row icon={icon} label={label} value="غير محدد" />
    );
  }
  return (
    <Row
      icon={icon}
      label={label}
      value={
        <span>
          <span className="block">{formatDateTime(iso)}</span>
          <span className="block text-[10px] text-slate-400 font-normal mt-0.5">
            بتوقيت السعودية: {formatSaudiTime(iso)}
          </span>
        </span>
      }
    />
  );
};

// صور مفقودة → عنصر بديل بنفس النسبة
const ImagePlaceholder = ({ name }) => (
  <div className="w-full aspect-[16/9] bg-gradient-to-br from-[#12224F] via-[#0D1A3E] to-[#0A1128] flex flex-col items-center justify-center gap-2">
    <ImageOff className="w-10 h-10 text-[#C9A96A]/40" />
    <span className="text-[#C9A96A]/70 text-xs font-semibold">
      {name}
    </span>
  </div>
);

const EventDetailsModal = ({ event, open, onOpenChange, onCalculate }) => {
  if (!event) return null;
  const status = getEventStatus(event);
  const imageUrl = resolveImageUrl(event.image);
  const target =
    status === "upcoming"
      ? event.startsAt
      : status === "live"
      ? event.endsAt
      : null;
  const countdownLabel =
    status === "upcoming"
      ? "تتوفر خلال"
      : status === "live"
      ? "تنتهي خلال"
      : null;

  const weaponLabel = Array.isArray(event.mainWeapon) && event.mainWeapon.length > 1
    ? "الأسلحة الرئيسية"
    : "السلاح الرئيسي";
  const characterLabel = Array.isArray(event.character) && event.character.length > 1
    ? "الشخصيات"
    : "الشخصية";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        dir="rtl"
        className="max-w-2xl w-[calc(100vw-1.5rem)] p-0 gap-0 bg-gradient-to-b from-[#0F1D42] to-[#0A1128] border border-[#C9A96A]/20 text-white overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        <div className="relative">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={event.name}
              className="w-full aspect-[16/9] object-cover"
            />
          ) : (
            <ImagePlaceholder name={event.name} />
          )}
          <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/70 to-transparent" />
          <div className="absolute top-3 start-3">
            <StatusPill status={status} />
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          <DialogHeader className="space-y-1 text-start">
            <DialogTitle className="text-white text-2xl font-black text-start">
              {event.name}
            </DialogTitle>
            <p className="text-[#C9A96A] text-xs font-semibold">{event.type}</p>
            <DialogDescription className="sr-only">
              تفاصيل حدث {event.name} — {event.type}
            </DialogDescription>
          </DialogHeader>

          {target && countdownLabel && (
            <CountdownRow target={target} label={countdownLabel} />
          )}
          {status === "ended" && (
            <div className="rounded-lg bg-slate-800/40 border border-slate-700/50 text-slate-300 text-sm text-center py-2.5 font-semibold">
              انتهت العجلة
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Row
              icon={Sword}
              label={weaponLabel}
              value={event.mainWeapon}
              mono
            />
            <Row
              icon={User}
              label={characterLabel}
              value={event.character}
              mono
            />
            <DateRow icon={Calendar} label="تاريخ البداية" iso={event.startsAt} />
            <DateRow icon={CalendarCheck} label="تاريخ النهاية" iso={event.endsAt} />
            {event.durationDays != null && (
              <Row
                icon={Timer}
                label="مدة التوفر"
                value={`${event.durationDays} يوماً`}
              />
            )}
            {(event.meta || []).map((m, i) => (
              <Row
                key={`meta-${i}`}
                icon={CheckCircle2}
                label={m.label}
                value={m.value}
              />
            ))}
          </div>

          {(event.sections || []).length > 0 && (
            <div className="space-y-3">
              {event.sections.map((sec, i) => (
                <div
                  key={i}
                  className="rounded-xl bg-white/[0.02] border border-white/5 p-3"
                >
                  <div className="flex items-center gap-1.5 mb-2 text-[#C9A96A] text-xs font-bold">
                    <ListChecks className="w-3.5 h-3.5" />
                    {sec.title}
                  </div>
                  <ul className="space-y-1">
                    {sec.items.map((it, j) => (
                      <li
                        key={j}
                        className="flex items-start gap-1.5 text-slate-200 text-sm"
                      >
                        <span className="w-1 h-1 rounded-full bg-[#C9A96A] mt-2 flex-shrink-0" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          <div>
            <div className="text-slate-400 text-xs mb-1.5">الوصف</div>
            <p className="text-slate-200 text-sm leading-relaxed bg-white/[0.02] border border-white/5 rounded-xl p-3 whitespace-pre-wrap">
              {event.description}
            </p>
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
            <button
              type="button"
              onClick={() => onOpenChange?.(false)}
              className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl bg-white/[0.05] hover:bg-white/[0.08] border border-white/10 text-slate-200 font-semibold text-sm transition-colors"
            >
              إغلاق
            </button>
            {event.wheelId && (
              <button
                type="button"
                onClick={() => {
                  onCalculate?.(event);
                  onOpenChange?.(false);
                }}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-b from-[#D4B278] to-[#B08A4A] hover:from-[#E5CB94] hover:to-[#B08A4A] text-[#0A1128] font-bold text-sm transition-colors"
              >
                <CalcIcon className="w-4 h-4" />
                احسب التكلفة
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EventDetailsModal;
